/**
 * Class to handle scroll events and communicate with .NET methods.
 */
export default class ScrollListener {
  private throttleScrollHandlerId: number;
  private handlerRef: ((event: Event) => void) | null;

  constructor() {
    this.throttleScrollHandlerId = -1;
    // Needed as variable to remove the event listeners
    this.handlerRef = null;
  }

  /**
   * Subscribes to throttled scroll event.
   * @param dotnetReference - The .NET reference to invoke methods on.
   * @param selector - The CSS selector of the element to attach the scroll listener to.
   */
  listenForScroll(dotnetReference: any, selector: string | null): void {
    console.log('[Nucor.Iron.UI] listenForScroll:', { selector });
    // If selector is null, attach to document
    const element: Element | Document = selector
      ? document.querySelector(selector)!
      : document;

    this.handlerRef = this.throttleScrollHandler.bind(this, dotnetReference);
    // Add the event listener
    element.addEventListener('scroll', this.handlerRef, false);
  }

  /**
   * Throttles the scroll event handler to fire once every 100 ms.
   * @param dotnetReference - The .NET reference to invoke methods on.
   * @param event - The scroll event.
   */
  throttleScrollHandler(dotnetReference: any, event: Event): void {
    clearTimeout(this.throttleScrollHandlerId);

    this.throttleScrollHandlerId = window.setTimeout(
      this.scrollHandler.bind(this, dotnetReference, event),
      100
    );
  }

  /**
   * Handles the scroll event and passes the scroll information to the .NET method.
   * @param dotnetReference - The .NET reference to invoke methods on.
   * @param event - The scroll event.
   */
  scrollHandler(dotnetReference: any, event: Event): void {
    console.log('[Nucor.Iron.UI] scrollHandler:', { event });
    try {
      const element = event.target as HTMLElement;

      // Data to pass
      const scrollTop: number = element.scrollTop;
      const scrollHeight: number = element.scrollHeight;
      const scrollWidth: number = element.scrollWidth;
      const scrollLeft: number = element.scrollLeft;
      const nodeName: string = element.nodeName;

      // Get the boundingClientRect of the first child
      const firstChild: Element | null = element.firstElementChild;
      const firstChildBoundingClientRect: DOMRect = firstChild!.getBoundingClientRect();

      // Invoke C# method
      dotnetReference.invokeMethodAsync('RaiseOnScroll', {
        firstChildBoundingClientRect,
        scrollLeft,
        scrollTop,
        scrollHeight,
        scrollWidth,
        nodeName,
      });
    } catch (error) {
      console.log('[Nucor.Iron.UI] Error in scrollHandler:', { error });
    }
  }

  /**
   * Removes the scroll event listener.
   * @param selector - The CSS selector of the element to remove the scroll listener from.
   */
  cancelListener(selector: string | null): void {
    const element: Element | Document = selector
      ? document.querySelector(selector)!
      : document;

    element.removeEventListener('scroll', this.handlerRef!);
  }
}
